import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { BENCHMARKS, CURRENCIES, HEDGE_STATUSES, useInterestRates, defaultIndexForCcy } from '../utils/importFinancingUtils';

const BLANK = {
  bc_ref: '', lender_bank: '', currency: 'USD', amount_fcy: '',
  drawdown_date: '', tenor_days: 90, repayment_date: '',
  index_type: 'SOFR', index_rate: '', spread_rate: '', all_in_rate: '',
  hedge_status: 'Unhedged', hedged_amount_fcy: '', contracted_rate_inr: '',
  lc_ref: '',
};

export default function NewBCModal({ onClose, onSaved, companyId }) {
  const [form, setForm]     = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const { rates: interestRates } = useInterestRates();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Currency change → update index and auto-populate benchmark rate
  useEffect(() => {
    const newIndex = defaultIndexForCcy(form.currency);
    const rateCodeMap = { 'SOFR': 'FED_FUNDS', '€STR': 'ECB_DEPOSIT', 'SONIA': 'BOE_BASE', 'SORA': 'MAS_SORA' };
    const rateCode = rateCodeMap[newIndex];
    const overnightRate = rateCode && interestRates[rateCode]
      ? interestRates[rateCode].overnightRate : '';
    setForm(f => ({ ...f, index_type: newIndex, index_rate: newIndex === 'Fixed' ? '' : overnightRate }));
  }, [form.currency]); // eslint-disable-line

  // Auto-calc all-in
  useEffect(() => {
    const idx = parseFloat(form.index_rate) || 0;
    const spd = parseFloat(form.spread_rate) || 0;
    if (form.index_type === 'Fixed') {
      if (idx > 0) setForm(f => ({ ...f, all_in_rate: idx.toFixed(2) }));
    } else {
      if (idx > 0 || spd > 0) setForm(f => ({ ...f, all_in_rate: (idx + spd).toFixed(2) }));
    }
  }, [form.index_rate, form.spread_rate, form.index_type]); // eslint-disable-line

  // Auto-calc repayment date
  useEffect(() => {
    if (form.drawdown_date && form.tenor_days) {
      const d = new Date(form.drawdown_date);
      d.setDate(d.getDate() + parseInt(form.tenor_days));
      setForm(f => ({ ...f, repayment_date: d.toISOString().split('T')[0] }));
    }
  }, [form.drawdown_date, form.tenor_days]); // eslint-disable-line

  const bm = BENCHMARKS[form.currency] || { options: ['Fixed'], label: 'Fixed' };

  async function handleSave() {
    if (!form.bc_ref || !form.lender_bank || !form.amount_fcy || !form.all_in_rate) {
      setError('Please fill in all required fields.'); return;
    }
    setSaving(true); setError('');
    const { error: err } = await supabase.from('import_financing_bc').insert({
      company_id:          companyId,
      lc_ref:              form.lc_ref || null,
      bc_ref:              form.bc_ref,
      lender_bank:         form.lender_bank,
      currency:            form.currency,
      amount_fcy:          parseFloat(form.amount_fcy),
      drawdown_date:       form.drawdown_date || null,
      repayment_date:      form.repayment_date || null,
      tenor_days:          parseInt(form.tenor_days),
      index_type:          form.index_type,
      index_rate:          parseFloat(form.index_rate) || null,
      spread_rate:         parseFloat(form.spread_rate) || null,
      all_in_rate:         parseFloat(form.all_in_rate),
      hedge_status:        form.hedge_status,
      hedged_amount_fcy:   parseFloat(form.hedged_amount_fcy) || 0,
      contracted_rate_inr: parseFloat(form.contracted_rate_inr) || null,
      status:              'Active',
    });
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved();
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-[560px] max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-white">New Buyers Credit</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-xl">✕</button>
        </div>

        {/* BC Details */}
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-800 pb-1 mb-3">BC Details</div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <BCField label="BC Reference *" value={form.bc_ref} onChange={v => set('bc_ref', v)} placeholder="BC-2024-004" />
          <BCField label="Linked LC Ref (optional)" value={form.lc_ref} onChange={v => set('lc_ref', v)} placeholder="LC-2024-001" />
          <BCField label="Lender Bank *" value={form.lender_bank} onChange={v => set('lender_bank', v)} placeholder="e.g. DBS Bank" />
          <div>
            <label className="text-xs text-gray-400 block mb-1">Currency</label>
            <select value={form.currency} onChange={e => set('currency', e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white">
              {CURRENCIES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <BCField label="Amount (FCY) *" type="number" value={form.amount_fcy} onChange={v => set('amount_fcy', v)} placeholder="500000" />
          <BCField label="Drawdown Date" type="date" value={form.drawdown_date} onChange={v => set('drawdown_date', v)} />
          <BCField label="Tenor (days)" type="number" value={form.tenor_days} onChange={v => set('tenor_days', v)} placeholder="90" />
          <BCField label="Repayment Date" type="date" value={form.repayment_date} onChange={v => set('repayment_date', v)} />
        </div>

        {/* Pricing */}
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-800 pb-1 mb-3">Pricing</div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Index ({form.currency}-linked)</label>
            <select value={form.index_type}
              onChange={e => {
                const selected = e.target.value;
                const rateCodeMap = { 'SOFR': 'FED_FUNDS', '€STR': 'ECB_DEPOSIT', 'SONIA': 'BOE_BASE', 'SORA': 'MAS_SORA' };
                const rateCode = rateCodeMap[selected];
                const overnightRate = rateCode && interestRates[rateCode]
                  ? interestRates[rateCode].overnightRate : '';
                setForm(f => ({ ...f, index_type: selected, index_rate: selected === 'Fixed' ? '' : overnightRate }));
              }}
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white">
              {bm.options.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">
              {form.index_type !== 'Fixed' ? 'Indicative Benchmark Rate (%)' : 'Fixed Rate (%)'}
            </label>
            <input type="number" step="0.01"
              value={form.index_rate}
              placeholder={form.index_type === 'Fixed' ? 'Enter fixed rate' : ''}
              disabled={form.index_type !== 'Fixed'}
              onChange={e => set('index_rate', e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-60" />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">
              Spread (%) {form.index_type === 'Fixed' && <span className="text-gray-600">— not applicable</span>}
            </label>
            <input type="number" step="0.01" placeholder="0.15"
              value={form.spread_rate}
              disabled={form.index_type === 'Fixed'}
              onChange={e => set('spread_rate', e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-40" />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">All-in Cost (%) *</label>
            <input type="number" step="0.01" value={form.all_in_rate}
              onChange={e => set('all_in_rate', e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white font-medium" />
          </div>
        </div>

        {/* Hedge */}
        <div className="text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-800 pb-1 mb-3">Hedge</div>
        <div className="grid grid-cols-2 gap-3 mb-5">
          <div>
            <label className="text-xs text-gray-400 block mb-1">Hedge Status</label>
            <select value={form.hedge_status} onChange={e => set('hedge_status', e.target.value)}
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white">
              {HEDGE_STATUSES.map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <BCField label="Hedged Amount (FCY)" type="number" value={form.hedged_amount_fcy}
            onChange={v => set('hedged_amount_fcy', v)} placeholder="0"
            disabled={form.hedge_status === 'Unhedged'} />
          <BCField label="Contracted Rate (INR)" type="number" value={form.contracted_rate_inr}
            onChange={v => set('contracted_rate_inr', v)} placeholder="83.50"
            disabled={form.hedge_status === 'Unhedged'} />
        </div>

        {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-700 text-gray-400 rounded-lg hover:text-gray-200">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm bg-purple-700 hover:bg-purple-600 text-white rounded-lg disabled:opacity-50 transition-colors">
            {saving ? 'Saving...' : 'Save BC'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Defined OUTSIDE component so it never re-mounts on re-render ──
function BCField({ label, value, onChange, type = 'text', placeholder = '', disabled = false }) {
  return (
    <div>
      <label className="text-xs text-gray-400 block mb-1">{label}</label>
      <input type={type} value={value} placeholder={placeholder} disabled={disabled}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded-lg text-white disabled:opacity-40" />
    </div>
  );
}
